import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button, Input, Label, Textarea, Badge } from '@creanizer/ui'
import { Heart, X } from 'lucide-react'
import type { SavedDungeon } from '@creanizer/types'
import { useDungeonStore } from '../../../../stores/dungeonStore'

interface DungeonEditModalProps {
  dungeon: SavedDungeon | null
  onClose: () => void
}

export function DungeonEditModal({ dungeon, onClose }: DungeonEditModalProps) {
  const updateDungeon = useDungeonStore(state => state.updateDungeon)
  const [title, setTitle] = useState(dungeon?.title || '')
  const [memo, setMemo] = useState(dungeon?.memo || '')
  const [tags, setTags] = useState<string[]>(dungeon?.tags || [])
  const [newTag, setNewTag] = useState('')
  const [favorite, setFavorite] = useState(dungeon?.favorite || false)

  if (!dungeon) return null

  const handleSave = () => {
    updateDungeon(dungeon.id, {
      title,
      memo,
      tags,
      favorite,
    })
    onClose()
  }

  const handleAddTag = () => {
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag])
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  return (
    <Dialog open={!!dungeon} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ダンジョンの編集</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">タイトル</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ダンジョンのタイトル"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="memo">メモ</Label>
            <Textarea
              id="memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="ダンジョンに関するメモ"
              className="mt-1"
              rows={4}
            />
          </div>

          <div>
            <Label>タグ</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="タグを追加"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleAddTag}
                disabled={!newTag}
              >
                追加
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="pr-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={favorite ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFavorite(!favorite)}
            >
              <Heart className={`h-4 w-4 mr-1 ${favorite ? 'fill-current' : ''}`} />
              お気に入り
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" onClick={handleSave}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}